import type { Match, Player, PlayerStats, RankRow } from './types';

/* ==========================================================================
   Elo en seizoenen.

   Deze berekening staat ook in de database: recompute_ratings() in migratie
   001 en season_standings() in migratie 004. De twee moeten gelijk blijven —
   supabase/test-schema.mjs vergelijkt ze met elkaar.

   Start 800, K-factor 32, bij 2v2 telt het teamgemiddelde en krijgen beide
   teamleden dezelfde bijstelling.
   ========================================================================== */

export const START_RATING = 800;
export const K_FACTOR = 32;
/** Bij een nieuw seizoen gaat een kwart van je voorsprong op de start mee. */
export const SEASON_CARRY = 0.25;
/**
 * Welk deel van de winst de verliezer betaalt. Op 0,8 levert een potje dat +15
 * waard is de winnaar +15 op en kost het de verliezer 12.
 *
 * Daarmee is de rating geen nulsomspel meer: er komt bij elk potje een derde
 * van het bedrag bij in het geheel. Onderling blijven de verhoudingen kloppen,
 * maar de hele ladder kruipt langzaam omhoog. Op 1 is alles weer in evenwicht.
 *
 * Dezelfde waarde staat als verlies_deel() in migratie 029; die twee moeten
 * gelijk blijven.
 */
export const VERLIES_DEEL = 0.8;

function gemiddelde(lijst: number[]): number {
  if (!lijst.length) return 0;
  return lijst.reduce((a, b) => a + b, 0) / lijst.length;
}

export function chronologisch(potjes: Match[]): Match[] {
  return potjes.slice().sort((a, b) => {
    const verschil = new Date(a.playedAt).getTime() - new Date(b.playedAt).getTime();
    if (verschil !== 0) return verschil;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

export function verwachting(ratingA: number, ratingB: number): number {
  return 1 / (1 + Math.pow(10, (ratingB - ratingA) / 400));
}

/**
 * Hoe zwaar een potje telt, gegeven het doelpuntenverschil. 10-9 is bijna
 * kop-of-munt en telt licht; 10-0 telt anderhalf keer.
 *
 *     verschil 1  -> 0,75      verschil 7  -> 1,25
 *     verschil 4  -> 1,00      verschil 10 -> 1,50
 *
 * Het ijkpunt ligt op vier en niet op één: zo blijft de schaal ongeveer waar
 * hij is. Zou het lichtste potje al 1,0 wegen, dan zwellen alle ratings mee en
 * betekent 800 volgend jaar iets anders dan vandaag.
 *
 * Dezelfde formule staat als marge_gewicht() in migratie 026; die twee moeten
 * gelijk blijven en supabase/test-schema.mjs vergelijkt ze.
 */
export function margeGewicht(scoreA: number, scoreB: number): number {
  const verschil = Math.min(Math.max(Math.abs(scoreA - scoreB), 1), 10);
  return 0.75 + (0.75 * (verschil - 1)) / 9;
}

export interface VerloopPunt {
  at: string;
  rating: number;
  matchId: string;
}

export interface PotjeDelta {
  deltaA: number;
  deltaB: number;
  ratingA: number;
  ratingB: number;
}

export interface Replay {
  ratings: Record<string, number>;
  verloop: Record<string, VerloopPunt[]>;
  perPotje: Record<string, PotjeDelta>;
}

/** Speelt potjes chronologisch af vanaf de meegegeven beginratings. */
export function replay(potjes: Match[], beginRatings?: Record<string, number>): Replay {
  const ratings: Record<string, number> = { ...(beginRatings ?? {}) };
  const verloop: Record<string, VerloopPunt[]> = {};
  const perPotje: Record<string, PotjeDelta> = {};

  const huidig = (id: string): number => {
    if (!(id in ratings)) ratings[id] = START_RATING;
    return ratings[id];
  };

  for (const m of chronologisch(potjes)) {
    const ratingA = gemiddelde(m.teamA.map(huidig));
    const ratingB = gemiddelde(m.teamB.map(huidig));
    const verwacht = verwachting(ratingA, ratingB);
    const uitslagA = m.scoreA > m.scoreB ? 1 : 0;
    /*
     * Het bedrag hangt af van de verwachting en het doelpuntenverschil; wie
     * wint krijgt het helemaal, wie verliest betaalt er een deel van.
     */
    const bedrag = Math.abs(K_FACTOR * margeGewicht(m.scoreA, m.scoreB) * (uitslagA - verwacht));
    const winst = bedrag;
    const verlies = -bedrag * VERLIES_DEEL;
    const deltaA = uitslagA === 1 ? winst : verlies;
    const deltaB = uitslagA === 1 ? verlies : winst;

    perPotje[m.id] = { deltaA, deltaB, ratingA, ratingB };

    for (const id of m.teamA) ratings[id] = huidig(id) + deltaA;
    for (const id of m.teamB) ratings[id] = huidig(id) + deltaB;

    for (const id of [...m.teamA, ...m.teamB]) {
      (verloop[id] ??= []).push({ at: m.playedAt, rating: ratings[id], matchId: m.id });
    }
  }

  return { ratings, verloop, perPotje };
}

/* ---------- statistiek ---------- */

export function potjesVan(id: string, potjes: Match[]): Match[] {
  return potjes.filter((m) => m.teamA.includes(id) || m.teamB.includes(id));
}

export interface Kant {
  inA: boolean;
  eigen: number;
  ander: number;
  gewonnen: boolean;
  ploeggenoten: string[];
  tegenstanders: string[];
}

export function kantVan(m: Match, id: string): Kant {
  const inA = m.teamA.includes(id);
  return {
    inA,
    eigen: inA ? m.scoreA : m.scoreB,
    ander: inA ? m.scoreB : m.scoreA,
    gewonnen: inA ? m.scoreA > m.scoreB : m.scoreB > m.scoreA,
    ploeggenoten: (inA ? m.teamA : m.teamB).filter((x) => x !== id),
    tegenstanders: inA ? m.teamB : m.teamA
  };
}

export function spelerStats(id: string, potjes: Match[]): PlayerStats {
  const eigen = potjesVan(id, potjes);
  let gewonnen = 0;
  let voor = 0;
  let tegen = 0;

  for (const m of eigen) {
    const k = kantVan(m, id);
    if (k.gewonnen) gewonnen++;
    voor += k.eigen;
    tegen += k.ander;
  }

  const gespeeld = eigen.length;
  return {
    gespeeld,
    gewonnen,
    verloren: gespeeld - gewonnen,
    voor,
    tegen,
    saldo: voor - tegen,
    winstPct: gespeeld ? (gewonnen / gespeeld) * 100 : 0
  };
}

export function reeksVan(id: string, potjes: Match[]): { soort: 'winst' | 'verlies' | null; aantal: number } {
  const eigen = chronologisch(potjesVan(id, potjes)).reverse();
  if (!eigen.length) return { soort: null, aantal: 0 };
  const eerste = kantVan(eigen[0], id).gewonnen;
  let n = 0;
  for (const m of eigen) {
    if (kantVan(m, id).gewonnen !== eerste) break;
    n++;
  }
  return { soort: eerste ? 'winst' : 'verlies', aantal: n };
}

export function langsteReeks(id: string, potjes: Match[]): number {
  let langste = 0;
  let nu = 0;
  for (const m of chronologisch(potjesVan(id, potjes))) {
    if (kantVan(m, id).gewonnen) {
      nu++;
      if (nu > langste) langste = nu;
    } else {
      nu = 0;
    }
  }
  return langste;
}

/** Onderlinge balans; potjes waarin beiden in hetzelfde team stonden tellen niet mee. */
export function onderling(a: string, b: string, potjes: Match[]): { gespeeld: number; gewonnenA: number } {
  let gespeeld = 0;
  let gewonnenA = 0;

  for (const m of potjes) {
    const aInA = m.teamA.includes(a);
    const aInB = m.teamB.includes(a);
    const bInA = m.teamA.includes(b);
    const bInB = m.teamB.includes(b);
    if ((!aInA && !aInB) || (!bInA && !bInB)) continue;
    if ((aInA && bInA) || (aInB && bInB)) continue;
    gespeeld++;
    if (aInA ? m.scoreA > m.scoreB : m.scoreB > m.scoreA) gewonnenA++;
  }

  return { gespeeld, gewonnenA };
}

export interface Relatie {
  id: string;
  gespeeld: number;
  pct: number;
}

/** Beste ploeggenoot en lastigste tegenstander, vanaf drie gezamenlijke potjes. */
export function relatiesVan(
  id: string,
  potjes: Match[]
): { ploeggenoot: Relatie | null; boeman: Relatie | null; tegenIds: string[] } {
  const samen: Record<string, { gespeeld: number; gewonnen: number }> = {};
  const tegen: Record<string, { gespeeld: number; gewonnen: number }> = {};

  for (const m of potjesVan(id, potjes)) {
    const k = kantVan(m, id);
    for (const p of k.ploeggenoten) {
      const rij = (samen[p] ??= { gespeeld: 0, gewonnen: 0 });
      rij.gespeeld++;
      if (k.gewonnen) rij.gewonnen++;
    }
    for (const t of k.tegenstanders) {
      const rij = (tegen[t] ??= { gespeeld: 0, gewonnen: 0 });
      rij.gespeeld++;
      if (k.gewonnen) rij.gewonnen++;
    }
  }

  const beste = (map: typeof samen, hoogste: boolean): Relatie | null => {
    const rijen = Object.entries(map)
      .map(([pid, r]) => ({ id: pid, gespeeld: r.gespeeld, pct: (r.gewonnen / r.gespeeld) * 100 }))
      .filter((r) => r.gespeeld >= 3);
    if (!rijen.length) return null;
    rijen.sort((a, b) => (hoogste ? b.pct - a.pct : a.pct - b.pct));
    return rijen[0];
  };

  return {
    ploeggenoot: beste(samen, true),
    boeman: beste(tegen, false),
    tegenIds: Object.keys(tegen)
  };
}

/* ---------- ranglijst ---------- */

/**
 * Rating eerst; bij gelijke stand beslist het winstpercentage, en pas als ook
 * dat gelijk is de naam. Zonder die tweede maat zou de volgorde bij een
 * gelijkspel op alfabet worden bepaald, en dat voelt willekeurig.
 */
export function gelijkeStandOp(a: RankRow, b: RankRow): number {
  return (
    b.rating - a.rating ||
    b.stats.winstPct - a.stats.winstPct ||
    a.player.name.localeCompare(b.player.name)
  );
}

/**
 * Waarop twee spelers een plek delen: alleen als rating én winstpercentage
 * gelijk zijn. De rating is een kommagetal, dus vergelijken gaat via een
 * sleutel in plaats van met een min-operatie.
 */
export function standSleutel(r: RankRow): string {
  return `${r.rating.toFixed(6)}|${r.stats.winstPct}`;
}

export function ranglijst(players: Player[], potjes: Match[], ratings: Record<string, number>): RankRow[] {
  return players
    .map((player) => ({
      player,
      rating: ratings[player.id] ?? START_RATING,
      stats: spelerStats(player.id, potjes)
    }))
    .sort(gelijkeStandOp);
}

/* ---------- seizoenen: kwartalen met een zachte reset ---------- */

export function seizoenSleutel(datum: string | number | Date): string {
  const d = new Date(datum);
  return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
}

export function seizoenStart(sleutel: string): Date {
  const jaar = Number(sleutel.slice(0, 4));
  const kwartaal = Number(sleutel.slice(6));
  return new Date(jaar, (kwartaal - 1) * 3, 1, 0, 0, 0);
}

export function seizoenEind(sleutel: string): Date {
  const jaar = Number(sleutel.slice(0, 4));
  const kwartaal = Number(sleutel.slice(6));
  return new Date(jaar, kwartaal * 3, 1, 0, 0, 0);
}

export function seizoenLabel(sleutel: string): string {
  return `${sleutel.slice(6)}e kwartaal ${sleutel.slice(0, 4)}`;
}

export function huidigSeizoen(): string {
  return seizoenSleutel(new Date());
}

/** Alle kwartalen van het eerste potje tot nu, nieuwste eerst. */
export function alleSeizoenen(potjes: Match[]): string[] {
  if (!potjes.length) return [huidigSeizoen()];
  const eerste = chronologisch(potjes)[0];
  const sleutels: string[] = [];
  let loper = seizoenStart(seizoenSleutel(eerste.playedAt));
  const eind = new Date();
  while (loper <= eind) {
    sleutels.push(seizoenSleutel(loper));
    loper = new Date(loper.getFullYear(), loper.getMonth() + 3, 1);
  }
  return sleutels.reverse();
}

/**
 * De rekenkern van één kwartaal. De basisrating is een zachte reset: een kwart
 * van je voorsprong op de startrating gaat mee. Vanaf die basis wordt het
 * seizoen nagespeeld. Gelijk aan season_standings() in migratie 004.
 */
function seizoensSpel(sleutel: string, potjes: Match[]) {
  const start = seizoenStart(sleutel);
  const eind = seizoenEind(sleutel);

  const ervoor = potjes.filter((m) => new Date(m.playedAt) < start);
  const erin = potjes.filter((m) => {
    const t = new Date(m.playedAt);
    return t >= start && t < eind;
  });

  const eerder = replay(ervoor).ratings;
  const basis: Record<string, number> = {};
  for (const [id, rating] of Object.entries(eerder)) {
    basis[id] = START_RATING + (rating - START_RATING) * SEASON_CARRY;
  }

  return { erin, basis, uitkomst: replay(erin, basis) };
}

/**
 * De rating van elke speler binnen dit kwartaal. Anders dan seizoensStand()
 * staat ook wie dit kwartaal nog niet speelde erin, met zijn zachte reset: een
 * indeling moet ook van hem weten hoe sterk hij is.
 */
export function seizoensRatings(sleutel: string, potjes: Match[]): Record<string, number> {
  const { basis, uitkomst } = seizoensSpel(sleutel, potjes);
  return { ...basis, ...uitkomst.ratings };
}

/** Ranglijst van één kwartaal: wie er dit kwartaal aan tafel stond. */
export function seizoensStand(sleutel: string, potjes: Match[], zoekSpeler: (id: string) => Player): RankRow[] {
  const { erin, basis, uitkomst } = seizoensSpel(sleutel, potjes);
  const meegedaan = new Set<string>();
  for (const m of erin) for (const id of [...m.teamA, ...m.teamB]) meegedaan.add(id);

  return [...meegedaan]
    .map((id) => ({
      player: zoekSpeler(id),
      rating: uitkomst.ratings[id] ?? START_RATING,
      basis: basis[id] ?? START_RATING,
      stats: spelerStats(id, erin)
    }))
    .sort(gelijkeStandOp);
}

export interface ErelijstRij {
  sleutel: string;
  winnaar: RankRow;
  deelnemers: number;
}

/** Winnaars van afgeronde kwartalen. */
export function erelijst(potjes: Match[], zoekSpeler: (id: string) => Player): ErelijstRij[] {
  const nu = huidigSeizoen();
  return alleSeizoenen(potjes)
    .filter((k) => k !== nu)
    .map((sleutel) => {
      const stand = seizoensStand(sleutel, potjes, zoekSpeler);
      return { sleutel, winnaar: stand[0], deelnemers: stand.length };
    })
    .filter((r): r is ErelijstRij => Boolean(r.winnaar));
}

/* ---------- gedeelde plekken ---------- */

/**
 * Plaatsnummers waarbij een gelijke stand de plek deelt: twee keer 1 wordt
 * gevolgd door 3, niet door 2. De rijen moeten al gesorteerd zijn.
 *
 * `sleutel` bepaalt waarop gelijkheid wordt gemeten — meestal de rating, maar
 * bij een seizoensstand kan dat iets anders zijn.
 */
export function plekken<T>(rijen: T[], sleutel: (r: T) => number | string): number[] {
  const uit: number[] = [];
  rijen.forEach((r, i) => {
    uit.push(i > 0 && sleutel(r) === sleutel(rijen[i - 1]) ? uit[i - 1] : i + 1);
  });
  return uit;
}

/** Op welke plek staat één speler, gedeelde plekken meegerekend. */
export function plekVan(id: string, rijen: RankRow[]): number {
  const nrs = plekken(rijen, standSleutel);
  const i = rijen.findIndex((r) => r.player.id === id);
  return i < 0 ? 0 : nrs[i];
}

/** Hoeveel spelers delen deze plek? */
export function deeltPlek(nrs: number[], i: number): boolean {
  return nrs.filter((n) => n === nrs[i]).length > 1;
}

/* ---------- posities aan de tafel ---------- */

export type Positie = 'verdediger' | 'aanvaller';

/**
 * In een duo staat de eerste opgestelde speler achterin en de tweede voorin —
 * zo wordt het formulier ook ingevuld. Bij 1v1 bedien je beide stangen, dus
 * dan is er geen positie.
 */
export function positieVan(potje: Match, id: string): Positie | null {
  if (potje.mode !== '2v2') return null;
  const team = potje.teamA.includes(id) ? potje.teamA : potje.teamB.includes(id) ? potje.teamB : null;
  if (!team) return null;
  const i = team.indexOf(id);
  return i === 0 ? 'verdediger' : i === 1 ? 'aanvaller' : null;
}

export interface PositieStat {
  gespeeld: number;
  gewonnen: number;
  verloren: number;
  winstPct: number;
  /** Doelpunten van je eigen team terwijl jij op deze plek stond. */
  voor: number;
  tegen: number;
  /** Gemiddeld per potje, zodat plekken met ongelijke aantallen vergelijkbaar zijn. */
  voorGem: number;
  tegenGem: number;
  /** Gewonnen zonder tegendoelpunt. */
  nulAchter: number;
}

/** Hoe iemand het doet achterin tegenover voorin. Alleen 2v2 telt mee. */
export function positieStats(id: string, potjes: Match[]): Record<Positie, PositieStat> {
  const leeg = (): PositieStat => ({
    gespeeld: 0, gewonnen: 0, verloren: 0, winstPct: 0,
    voor: 0, tegen: 0, voorGem: 0, tegenGem: 0, nulAchter: 0
  });
  const uit: Record<Positie, PositieStat> = { verdediger: leeg(), aanvaller: leeg() };

  for (const m of potjes) {
    const pos = positieVan(m, id);
    if (!pos) continue;
    const bijA = m.teamA.includes(id);
    const eigen = bijA ? m.scoreA : m.scoreB;
    const ander = bijA ? m.scoreB : m.scoreA;
    const gewonnen = eigen > ander;
    const s = uit[pos];
    s.gespeeld += 1;
    if (gewonnen) s.gewonnen += 1;
    else s.verloren += 1;
    s.voor += eigen;
    s.tegen += ander;
    if (gewonnen && ander === 0) s.nulAchter += 1;
  }

  for (const pos of ['verdediger', 'aanvaller'] as Positie[]) {
    const s = uit[pos];
    s.winstPct = s.gespeeld ? Math.round((s.gewonnen / s.gespeeld) * 100) : 0;
    s.voorGem = s.gespeeld ? Math.round((s.voor / s.gespeeld) * 10) / 10 : 0;
    s.tegenGem = s.gespeeld ? Math.round((s.tegen / s.gespeeld) * 10) / 10 : 0;
  }
  return uit;
}

/**
 * Welke positie iemand het beste ligt. Pas een oordeel vanaf drie potjes aan
 * beide kanten: daaronder zegt een percentage niets.
 */
export function bestePositie(stat: Record<Positie, PositieStat>): Positie | null {
  const { verdediger, aanvaller } = stat;
  if (verdediger.gespeeld < 3 || aanvaller.gespeeld < 3) return null;
  if (verdediger.winstPct === aanvaller.winstPct) return null;
  return verdediger.winstPct > aanvaller.winstPct ? 'verdediger' : 'aanvaller';
}

/**
 * Wie doet het op deze plek het beste? Gesorteerd op winstpercentage, met een
 * ondergrens aan potjes zodat één gelukkige avond niemand bovenaan zet.
 */
export function positieRanglijst(
  spelers: Player[],
  potjes: Match[],
  positie: Positie,
  minimum = 3
): { player: Player; stat: PositieStat }[] {
  return spelers
    .map((player) => ({ player, stat: positieStats(player.id, potjes)[positie] }))
    .filter((r) => r.stat.gespeeld >= minimum)
    .sort(
      (a, b) =>
        b.stat.winstPct - a.stat.winstPct ||
        b.stat.gewonnen - a.stat.gewonnen ||
        a.player.name.localeCompare(b.player.name)
    );
}

/**
 * Hoeveel scheelt de ene plek met de andere, in procentpunten winst. Positief
 * betekent: achterin sterker. Null zolang er te weinig potjes zijn.
 */
export function positieVerschil(stat: Record<Positie, PositieStat>): number | null {
  if (stat.verdediger.gespeeld < 3 || stat.aanvaller.gespeeld < 3) return null;
  return stat.verdediger.winstPct - stat.aanvaller.winstPct;
}
